import React, { useMemo, useState, useRef } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import type { CommerceSelectOptionViewModel } from "../../contracts/commerce-admin";
import { cn } from "../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../../internal/primitives/popover";

type CommerceComboboxBaseProps = {
  options: CommerceSelectOptionViewModel[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
};

type CommerceComboboxProps = CommerceComboboxBaseProps & {
  value?: string | null;
  onChange: (value: string) => void;
  allowClear?: boolean;
};

type CommerceMultiComboboxProps = CommerceComboboxBaseProps & {
  values: string[];
  onChange: (values: string[]) => void;
};

function normalizeText(value: string): string {
  return value.trim().toLowerCase();
}

function optionMatchesQuery(option: CommerceSelectOptionViewModel, query: string): boolean {
  if (!query) {
    return true;
  }
  const haystack = [
    option.label,
    option.description,
    option.helperText,
    option.badge,
    option.groupLabel,
    ...(option.keywords || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

function filterOptions(options: CommerceSelectOptionViewModel[], query: string) {
  const normalizedQuery = normalizeText(query);
  return options.filter(option => optionMatchesQuery(option, normalizedQuery));
}

function groupOptions(options: CommerceSelectOptionViewModel[]) {
  const groups = new Map<string, CommerceSelectOptionViewModel[]>();
  options.forEach(option => {
    const groupLabel = option.groupLabel?.trim() || "";
    const existing = groups.get(groupLabel);
    if (existing) {
      existing.push(option);
      return;
    }
    groups.set(groupLabel, [option]);
  });
  return Array.from(groups.entries());
}

export function CommerceCombobox({
  options,
  value,
  onChange,
  placeholder = "Select an option...",
  searchPlaceholder = "Search...",
  emptyMessage = "No options found.",
  disabled = false,
  loading = false,
  className,
  allowClear = true,
}: CommerceComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedOption = useMemo(
    () => options.find(option => option.value === (value || "")),
    [options, value],
  );

  const filteredOptions = useMemo(() => filterOptions(options, query), [options, query]);
  const groupedOptions = useMemo(() => groupOptions(filteredOptions), [filteredOptions]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setQuery("");
    }
  };

  return (
    <Popover open={open} onOpenChange={disabled || loading ? undefined : handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled || loading}
          className={cn(
            "flex min-h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          aria-expanded={open}
        >
          <div className="flex-1 flex flex-col items-start min-w-0 text-left">
            {loading ? (
              <span className="truncate text-muted-foreground">Loading...</span>
            ) : selectedOption ? (
              <div className="flex flex-col min-w-0 w-full">
                <span className="truncate font-medium text-foreground">{selectedOption.label}</span>
                {selectedOption.description && (
                  <span className="truncate text-[10px] text-muted-foreground">{selectedOption.description}</span>
                )}
              </div>
            ) : (
              <span className="truncate text-muted-foreground">{placeholder}</span>
            )}
          </div>
          
          <div className="flex shrink-0 items-center gap-1">
            {allowClear && value && !disabled && !loading ? (
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("");
                }}
                className="flex h-5 w-5 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </div>
            ) : null}
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground opacity-50" />
          </div>
        </button>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-[var(--radix-popover-trigger-width)] p-0 shadow-lg border border-border/80" 
        align="start"
      >
        <div className="flex items-center border-b border-border/50 px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground opacity-50" />
          <input
            ref={inputRef}
            className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        
        <div className="max-h-[300px] overflow-y-auto p-1">
          {groupedOptions.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          ) : (
            groupedOptions.map(([groupLabel, group]) => (
              <div key={groupLabel || "default"}>
                {groupLabel && (
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    {groupLabel}
                  </div>
                )}
                {group.map(option => {
                  const isSelected = value === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      disabled={option.disabled}
                      onClick={() => {
                        onChange(option.value);
                        setOpen(false);
                      }}
                      className={cn(
                        "relative flex w-full cursor-default select-none items-start rounded-sm px-2 py-2 text-sm outline-none transition-colors",
                        isSelected ? "bg-primary/10 text-primary font-medium" : "hover:bg-accent hover:text-accent-foreground text-foreground",
                        option.disabled && "pointer-events-none opacity-50"
                      )}
                    >
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center mr-2">
                        {isSelected && <Check className="h-4 w-4" />}
                      </div>
                      <div className="flex flex-col items-start text-left min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate">{option.label}</span>
                          {option.badge && (
                            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                              {option.badge}
                            </span>
                          )}
                        </div>
                        {option.description && (
                          <span className="mt-0.5 text-xs text-muted-foreground line-clamp-1">{option.description}</span>
                        )}
                        {option.helperText && (
                          <span className="mt-0.5 text-[10px] text-muted-foreground/70 uppercase tracking-wider">{option.helperText}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function CommerceMultiCombobox({
  options,
  values,
  onChange,
  placeholder = "Select options...",
  searchPlaceholder = "Search...",
  emptyMessage = "No options found.",
  disabled = false,
  loading = false,
  className,
}: CommerceMultiComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedValuesSet = useMemo(() => new Set(values), [values]);
  
  const selectedOptions = useMemo(
    () => values.map(v => options.find(o => o.value === v)).filter(Boolean) as CommerceSelectOptionViewModel[],
    [options, values],
  );

  const filteredOptions = useMemo(() => filterOptions(options, query), [options, query]);
  const groupedOptions = useMemo(() => groupOptions(filteredOptions), [filteredOptions]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setQuery("");
    }
  };

  const handleSelect = (optionValue: string) => {
    if (selectedValuesSet.has(optionValue)) {
      onChange(values.filter(v => v !== optionValue));
    } else {
      onChange([...values, optionValue]);
    }
  };

  const handleRemove = (e: React.MouseEvent, optionValue: string) => {
    e.stopPropagation();
    onChange(values.filter(v => v !== optionValue));
  };

  return (
    <Popover open={open} onOpenChange={disabled || loading ? undefined : handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled || loading}
          className={cn(
            "flex min-h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          aria-expanded={open}
        >
          <div className="flex-1 flex flex-wrap items-center gap-1.5 min-w-0 text-left">
            {loading ? (
              <span className="truncate text-muted-foreground">Loading...</span>
            ) : selectedOptions.length > 0 ? (
              selectedOptions.map(option => (
                <span
                  key={option.value}
                  className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-xs font-medium text-secondary-foreground"
                >
                  <span className="truncate max-w-[150px]">{option.label}</span>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => handleRemove(e, option.value)}
                    className="flex h-3.5 w-3.5 items-center justify-center rounded-sm hover:bg-muted-foreground/20"
                  >
                    <X className="h-2.5 w-2.5" />
                  </div>
                </span>
              ))
            ) : (
              <span className="truncate text-muted-foreground">{placeholder}</span>
            )}
          </div>
          
          <div className="flex shrink-0 items-center gap-1 ml-2">
            {values.length > 0 && !disabled && !loading ? (
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange([]);
                }}
                className="flex h-5 w-5 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </div>
            ) : null}
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground opacity-50" />
          </div>
        </button>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-[var(--radix-popover-trigger-width)] p-0 shadow-lg border border-border/80" 
        align="start"
      >
        <div className="flex items-center border-b border-border/50 px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground opacity-50" />
          <input
            className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        
        <div className="max-h-[300px] overflow-y-auto p-1">
          {groupedOptions.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          ) : (
            groupedOptions.map(([groupLabel, group]) => (
              <div key={groupLabel || "default"}>
                {groupLabel && (
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    {groupLabel}
                  </div>
                )}
                {group.map(option => {
                  const isSelected = selectedValuesSet.has(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      disabled={option.disabled}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSelect(option.value);
                      }}
                      className={cn(
                        "relative flex w-full cursor-default select-none items-start rounded-sm px-2 py-2 text-sm outline-none transition-colors",
                        isSelected ? "bg-primary/5 text-primary" : "hover:bg-accent hover:text-accent-foreground text-foreground",
                        option.disabled && "pointer-events-none opacity-50"
                      )}
                    >
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center mr-2">
                        <div className={cn(
                          "flex h-4 w-4 items-center justify-center rounded border",
                          isSelected ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/30 text-transparent"
                        )}>
                          <Check className="h-3 w-3" />
                        </div>
                      </div>
                      <div className="flex flex-col items-start text-left min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate">{option.label}</span>
                          {option.badge && (
                            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                              {option.badge}
                            </span>
                          )}
                        </div>
                        {option.description && (
                          <span className="mt-0.5 text-xs text-muted-foreground line-clamp-1">{option.description}</span>
                        )}
                        {option.helperText && (
                          <span className="mt-0.5 text-[10px] text-muted-foreground/70 uppercase tracking-wider">{option.helperText}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
